import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import CatalogSelect from './CatalogSelect';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel, SelectSeparator } from './ui/select';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { DateRangeFilter } from './ui/date-range-filter';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import { Search, SlidersHorizontal, RefreshCw, Download, X, Check, GraduationCap, Building2, Calendar, User, Clock, ChevronRight, ChevronLeft, Plus, MapPin, Phone } from 'lucide-react';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

// Color scheme - matching brand colors
const COLORS = {
  primary: '#192650',
  accent: '#0DA891',
  accentLight: '#E8F7F5',
  background: '#F8FAFC',
  textSecondary: '#64748b',
  border: '#e2e8f0',
  cardBg: '#ffffff',
};

// Universal 9-stage admission lifecycle - No skipping allowed
const STAGES = [
  { key: 'registration', label: 'Registration', short: 'Reg', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  { key: 'application_submitted', label: 'Application Submitted', short: 'App', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  { key: 'offer_letter_received', label: 'Offer Letter Received', short: 'Offer', color: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
  { key: 'documentation_process', label: 'Documentation', short: 'Docs', color: 'bg-purple-100 text-purple-700 border-purple-300' },
  { key: 'ministry_approval', label: 'Ministry Approval', short: 'Ministry', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { key: 'visa_application_submitted', label: 'Visa App Submitted', short: 'Visa App', color: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
  { key: 'visa_approved', label: 'Visa Approved', short: 'Approved', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { key: 'visa_denied', label: 'Visa Denied', short: 'Denied', color: 'bg-red-100 text-red-700 border-red-300' },
  { key: 'ticket_travel_date', label: 'Ticket & Travel', short: 'Ticket', color: 'bg-sky-100 text-sky-700 border-sky-300' },
  { key: 'student_flown', label: 'Student Flown', short: 'Flown', color: 'bg-teal-100 text-teal-700 border-teal-300' }
];

// Status tabs
const STATUS_TABS = [
  { id: 'active', label: 'Active', color: 'bg-green-500' },
  { id: 'flown', label: 'Flown', color: 'bg-teal-500' },
  { id: 'cancelled', label: 'Cancelled', color: 'bg-red-500' },
  { id: 'deferred', label: 'Deferred', color: 'bg-amber-500' },
];

export default function AdmissionTileView() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusCounts, setStatusCounts] = useState({});
  const [pagination, setPagination] = useState({ page: 1, limit: 24, total: 0, total_pages: 1 });
  
  // Filters
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [stageFilter, setStageFilter] = useState('all');
  const [intakeFilter, setIntakeFilter] = useState('all');
  const [counsellorFilter, setCounsellorFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [programFilter, setProgramFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState({ startDate: null, endDate: null, preset: 'all' });
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  
  // Data for filters
  const [intakes, setIntakes] = useState([]);
  const [counsellors, setCounsellors] = useState([]);
  const [countries, setCountries] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [activeIntakes, setActiveIntakes] = useState([]);
  
  // Selected student for stage dialog
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  
  // Add Student Dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addingStudent, setAddingStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({
    full_name: '', phone: '', email: '', date_of_birth: '',
    desired_course: '', desired_country: '', university_selected: '', intake: '', notes: '',
    source: '', assigned_counselor_id: ''
  });

  const handleAddStudent = async () => {
    const mandatory = [
      { field: 'full_name', label: 'Full Name' },
      { field: 'phone', label: 'Phone' },
      { field: 'email', label: 'Email' },
      { field: 'date_of_birth', label: 'Date of Birth' },
      { field: 'desired_course', label: 'Program / Course' },
      { field: 'desired_country', label: 'Country' },
      { field: 'university_selected', label: 'University' },
      { field: 'intake', label: 'Intake' },
      { field: 'source', label: 'Source' }
    ];
    const missing = mandatory.filter(m => !newStudent[m.field]?.trim()).map(m => m.label);
    if (missing.length > 0) {
      toast.error(`Missing: ${missing.join(', ')}`);
      return;
    }
    setAddingStudent(true);
    try {
      await apiClient.post('/student-portal/direct-admission', newStudent);
      toast.success('Student added to admissions');
      setAddDialogOpen(false);
      setNewStudent({ full_name: '', phone: '', email: '', date_of_birth: '', desired_course: '', desired_country: '', university_selected: '', intake: '', notes: '', source: '', assigned_counselor_id: '' });
      fetchStudents();
      fetchStats();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.message : (detail || 'Failed to add student'));
    } finally {
      setAddingStudent(false);
    }
  };

  
  // Reassign dialog state
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassignStudent, setReassignStudent] = useState(null);
  const [newCounsellorId, setNewCounsellorId] = useState('');
  const [reassigning, setReassigning] = useState(false);

  // Missing (force-converted) admissions are now reconciled automatically on load —
  // no manual "Fix Missing" button needed.
  const autoRepairRan = useRef(false);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        sort_by: 'updated_at',
        sort_order: 'desc'
      });
      
      if (search) params.append('search', search);
      if (activeTab !== 'all') params.append('status', activeTab);
      if (stageFilter !== 'all') params.append('stage', stageFilter);
      if (intakeFilter !== 'all') params.append('intake', intakeFilter);
      if (counsellorFilter !== 'all') params.append('counsellor_id', counsellorFilter);
      if (countryFilter !== 'all') params.append('country', countryFilter);
      if (programFilter !== 'all') params.append('program', programFilter);
      if (dateFilter.startDate) params.append('start_date', dateFilter.startDate);
      if (dateFilter.endDate) params.append('end_date', dateFilter.endDate);
      
      const response = await apiClient.get(`/student-portal/list?${params}`);
      setStudents(response.data.data);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        total_pages: response.data.pagination.total_pages
      }));
      if (response.data.status_counts) {
        setStatusCounts(response.data.status_counts);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, search, activeTab, stageFilter, intakeFilter, counsellorFilter, countryFilter, programFilter, dateFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiClient.get('/student-portal/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchIntakes = useCallback(async () => {
    try {
      const response = await apiClient.get('/system-settings/intakes/all');
      setIntakes(response.data.intakes || []);
    } catch (error) {
      console.error('Error fetching intakes:', error);
    }
  }, []);

  const fetchCounsellors = useCallback(async () => {
    try {
      const response = await apiClient.get('/users/counsellors');
      setCounsellors(response.data || []);
    } catch (error) {
      console.error('Error fetching counsellors:', error);
    }
  }, []);

  const fetchCountries = useCallback(async () => {
    try {
      const response = await apiClient.get('/student-portal/distinct/countries');
      setCountries(response.data.countries || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  }, []);

  const fetchPrograms = useCallback(async () => {
    try {
      const response = await apiClient.get('/student-portal/distinct/programs');
      setPrograms(response.data.programs || []);
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  }, []);

  // Full catalog (programs, universities, countries) for the Add Student dropdowns
  const fetchCatalog = useCallback(async () => {
    try {
      const response = await apiClient.get('/constants');
      if (Array.isArray(response.data?.programs)) setPrograms(prev => prev.length ? prev : response.data.programs);
      if (Array.isArray(response.data?.countries)) setCountries(prev => prev.length ? prev : response.data.countries);
      setUniversities(response.data?.universities || []);
    } catch (error) {
      console.error('Error fetching catalog:', error);
    }
  }, []);

  const fetchActiveIntakes = useCallback(async () => {
    try {
      const response = await apiClient.get('/system-settings/intakes/selectable');
      setActiveIntakes(response.data?.intakes || []);
    } catch (error) {
      console.error('Error fetching active intakes:', error);
    }
  }, []);

  useEffect(() => {
    fetchStudents();
    fetchStats();
    fetchIntakes();
    fetchCounsellors();
    fetchCountries();
    fetchPrograms();
    fetchCatalog();
    fetchActiveIntakes();
  }, [fetchStudents, fetchStats, fetchIntakes, fetchCounsellors, fetchCountries, fetchPrograms, fetchCatalog, fetchActiveIntakes]);

  // Silently reconcile force-converted leads that are missing from the list, once on load.
  useEffect(() => {
    if (autoRepairRan.current) return;
    if (!(user?.role === 'admin' || user?.role === 'operating_head')) return;
    autoRepairRan.current = true;
    (async () => {
      try {
        const res = await apiClient.post('/admin/repair-converted-admissions');
        if ((res.data?.repaired_count || 0) > 0) {
          fetchStudents();
          fetchStats();
        }
      } catch (_) { /* non-fatal — list still renders */ }
    })();
  }, [user, fetchStudents, fetchStats]);

  // Replaced by the single useEffect above

  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [activeTab, stageFilter, search, intakeFilter, counsellorFilter, countryFilter, programFilter, dateFilter]);

  const getStageConfig = (stage) => STAGES.find(s => s.key === stage) || STAGES[0];
  const getStageIndex = (stage) => STAGES.findIndex(s => s.key === stage);

  const getActiveFilterCount = () => {
    let count = 0;
    if (stageFilter !== 'all') count++;
    if (intakeFilter !== 'all') count++;
    if (counsellorFilter !== 'all') count++;
    if (dateFilter.preset !== 'all') count++;
    return count;
  };

  const clearAllFilters = () => {
    setStageFilter('all');
    setIntakeFilter('all');
    setCounsellorFilter('all');
    setDateFilter({ startDate: null, endDate: null, preset: 'all' });
    setSearch('');
  };

  const openStudentProfile = (student) => {
    // Navigate to the student portal which has Finance, Documents, Stages tabs
    navigate(`/student-portal/${student.id}`);
  };

  const openStageDialog = (e, student) => {
    e.stopPropagation();
    setSelectedStudent(student);
    setStageDialogOpen(true);
  };

  const handleStageUpdate = async (newStage) => {
    if (!selectedStudent) return;
    
    try {
      await apiClient.put(`/student-portal/${selectedStudent.id}/stage`, { stage: newStage });
      toast.success('Stage updated successfully');
      setStageDialogOpen(false);
      fetchStudents();
      fetchStats();
    } catch (error) {
      console.error('Error updating stage:', error);
      toast.error('Failed to update stage');
    }
  };

  // Reassign lead to another counselor
  const openReassignDialog = (e, student) => {
    e.stopPropagation();
    setReassignStudent(student);
    setNewCounsellorId('');
    setReassignDialogOpen(true);
  };

  const handleReassign = async () => {
    if (!reassignStudent || !newCounsellorId) {
      toast.error('Please select a counselor');
      return;
    }
    
    setReassigning(true);
    try {
      await apiClient.post(`/students/${reassignStudent.id}/transfer`, {
        new_counselor_id: newCounsellorId
      });
      toast.success('Lead reassigned successfully');
      setReassignDialogOpen(false);
      setReassignStudent(null);
      fetchStudents();
    } catch (error) {
      console.error('Error reassigning lead:', error);
      toast.error(error.response?.data?.detail || 'Failed to reassign lead');
    } finally {
      setReassigning(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  // Filter content component for reuse - matching Leads module layout
  const FilterContent = ({ isMobile }) => (
    <div className={isMobile ? "space-y-4" : "flex items-center gap-2 flex-wrap"}>
      {/* Date Filter */}
      <div className={isMobile ? '' : 'shrink-0'}>
        <DateRangeFilter 
          onDateChange={setDateFilter}
          defaultPreset={dateFilter.preset}
          compact={true}
        />
      </div>
      
      {/* Stage Filter */}
      <div className={isMobile ? '' : 'shrink-0'}>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className={isMobile ? "w-full h-10" : "w-[110px] h-8 text-xs"} style={{ borderColor: COLORS.border }} data-testid="stage-filter">
            <SelectValue placeholder="Stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {STAGES.map(s => (
              <SelectItem key={s.key} value={s.key}>{s.short}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Intake Filter */}
      <div className={isMobile ? '' : 'shrink-0'}>
        <Select value={intakeFilter} onValueChange={setIntakeFilter}>
          <SelectTrigger className={isMobile ? "w-full h-10" : "w-[120px] h-8 text-xs"} style={{ borderColor: COLORS.border }} data-testid="intake-filter">
            <SelectValue placeholder="Intake" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Intakes</SelectItem>
            <SelectSeparator />
            {['Active', 'Upcoming', 'Closed'].map(status => {
              const group = intakes.filter(i => i.status === status);
              if (group.length === 0) return null;
              return (
                <SelectGroup key={status}>
                  <SelectLabel className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{status}</SelectLabel>
                  {group.map(i => (
                    <SelectItem key={i.id} value={i.name}>
                      <span className="flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${status === 'Active' ? 'bg-green-500' : status === 'Upcoming' ? 'bg-blue-500' : 'bg-gray-400'}`}></span>
                        {i.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectGroup>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Country Filter */}
      <div className={isMobile ? '' : 'shrink-0'}>
        <Select value={countryFilter} onValueChange={setCountryFilter}>
          <SelectTrigger className={isMobile ? "w-full h-10" : "w-[120px] h-8 text-xs"} style={{ borderColor: COLORS.border }} data-testid="country-filter">
            <SelectValue placeholder="Country" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Countries</SelectItem>
            {countries.map(c => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Program Filter */}
      <div className={isMobile ? '' : 'shrink-0'}>
        <Select value={programFilter} onValueChange={setProgramFilter}>
          <SelectTrigger className={isMobile ? "w-full h-10" : "w-[110px] h-8 text-xs"} style={{ borderColor: COLORS.border }} data-testid="program-filter">
            <SelectValue placeholder="Program" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Programs</SelectItem>
            {programs.map(p => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Counsellor Filter */}
      <div className={isMobile ? '' : 'shrink-0'}>
        <Select value={counsellorFilter} onValueChange={setCounsellorFilter}>
          <SelectTrigger className={isMobile ? "w-full h-10" : "w-[130px] h-8 text-xs"} style={{ borderColor: COLORS.border }} data-testid="counsellor-filter">
            <SelectValue placeholder="Counsellor" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Counsellors</SelectItem>
            {counsellors.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.background }}>
      {/* Sticky wrapper: Tabs + Filters stay pinned on phones when switching tabs */}
      <div className="sticky top-14 sm:top-16 z-20">
      {/* Header - Consistent Navy Style */}
      <div className="rounded-xl mx-2 sm:mx-4 mt-2 sm:mt-4" style={{ backgroundColor: COLORS.primary }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: COLORS.accent }}>
                <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-semibold text-white truncate">Admissions</h1>
                <p className="text-[10px] sm:text-xs text-white/70 truncate">
                  {Object.keys(statusCounts).length > 0
                    ? (statusCounts.active || 0) + (statusCounts.flown || 0) + (statusCounts.cancelled || 0) + (statusCounts.deferred || 0)
                    : (stats?.total || 0)} total students
                </p>
              </div>
            </div>
            
            {/* Add Student Button - Admin & Doc Team only */}
            <div className="flex items-center gap-2 shrink-0">
            {(user?.role === 'admin' || user?.role === 'operating_head' || user?.role === 'documentation_team') && (
              <Button
                data-testid="add-admission-btn"
                onClick={() => setAddDialogOpen(true)}
                size="sm"
                className="h-8 px-3 text-xs font-medium bg-white/20 hover:bg-white/30 text-white border-0"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Add Student
              </Button>
            )}
            </div>
          </div>
        </div>

        {/* Status Tabs - Inside Navy Header */}
        <div style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} className="rounded-b-xl">
          <div className="max-w-7xl mx-auto px-2 sm:px-4">
            <div className="flex items-center gap-1 py-2 overflow-x-auto scrollbar-hide">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center shrink-0 gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg text-[10px] sm:text-xs font-medium whitespace-nowrap transition-all
                    ${activeTab === tab.id 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-white/80 hover:bg-white/10'
                    }`}
                  data-testid={`tab-${tab.id}`}
                >
                  <span className={`w-2 h-2 rounded-full ${tab.color}`}></span>
                  {tab.label}
                  <span className={`ml-1 min-w-[18px] px-1.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-semibold
                    ${activeTab === tab.id ? 'bg-gray-100' : 'bg-white/20'}`}>
                    {statusCounts[tab.id] ?? (stats?.by_status?.[tab.id] || 0)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Filters Row */}
      <div className="border-b" style={{ backgroundColor: COLORS.cardBg, borderColor: COLORS.border }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 space-y-2">
          {/* Row 1: Search + Mobile Actions + Desktop Refresh */}
          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative flex-1 min-w-[180px] sm:max-w-[220px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <Input
                placeholder="Search name, ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 pl-8 pr-2 text-xs"
                style={{ borderColor: COLORS.border }}
                data-testid="search-input"
              />
            </div>

            {/* Mobile: Filter Button */}
            <div className="flex sm:hidden items-center gap-1.5">
              <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
                <SheetTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-8 px-2.5 relative"
                    style={{ borderColor: getActiveFilterCount() > 0 ? COLORS.accent : COLORS.border }}
                    data-testid="mobile-filter-btn"
                  >
                    <SlidersHorizontal className="w-3.5 h-3.5" style={{ color: getActiveFilterCount() > 0 ? COLORS.accent : undefined }} />
                    {getActiveFilterCount() > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center text-white"
                        style={{ backgroundColor: COLORS.accent }}>
                        {getActiveFilterCount()}
                      </span>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="h-auto max-h-[70vh] rounded-t-2xl">
                  <SheetHeader className="pb-4">
                    <SheetTitle className="flex items-center gap-2 text-base" style={{ color: COLORS.primary }}>
                      <SlidersHorizontal className="w-4 h-4" style={{ color: COLORS.accent }} />
                      Filter Admissions
                    </SheetTitle>
                    <SheetDescription className="text-xs">
                      {getActiveFilterCount() > 0 ? `${getActiveFilterCount()} filter(s) active` : 'No filters applied'}
                    </SheetDescription>
                  </SheetHeader>
                  <FilterContent isMobile={true} />
                  <SheetFooter className="flex flex-row gap-2 pt-4 mt-4 border-t">
                    {getActiveFilterCount() > 0 && (
                      <Button variant="outline" className="flex-1 h-10" onClick={clearAllFilters}>
                        <X className="w-4 h-4 mr-1.5" /> Clear All
                      </Button>
                    )}
                    <Button className="flex-1 h-10" style={{ backgroundColor: COLORS.accent }} onClick={() => setFilterSheetOpen(false)}>
                      <Check className="w-4 h-4 mr-1.5" /> Apply
                    </Button>
                  </SheetFooter>
                </SheetContent>
              </Sheet>
              
              <Button variant="ghost" size="sm" onClick={fetchStudents} className="h-8 w-8 p-0">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {/* Desktop: Refresh - right aligned on row 1 */}
            <div className="hidden sm:flex items-center gap-1 ml-auto shrink-0">
              <Button variant="ghost" size="sm" onClick={fetchStudents} className="h-8">
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          {/* Row 2: Desktop Inline Filters */}
          <div className="hidden sm:flex items-center gap-2 flex-wrap">
            <FilterContent isMobile={false} />
            {getActiveFilterCount() > 0 && (
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="h-8 px-2 text-xs text-gray-500 shrink-0">
                <X className="w-3 h-3 mr-1" /> Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Admission Tiles Grid */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-2" style={{ borderColor: COLORS.accent, borderTopColor: 'transparent' }}></div>
          </div>
        ) : students.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: COLORS.accentLight }}>
              <GraduationCap className="w-7 h-7" style={{ color: COLORS.accent }} />
            </div>
            <h3 className="text-base font-medium" style={{ color: COLORS.primary }}>No students found</h3>
            <p className="text-sm mt-1" style={{ color: COLORS.textSecondary }}>
              {activeTab === 'active' ? 'No active admissions' :
               activeTab === 'cancelled' ? 'No cancelled admissions' :
               'No deferred students'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {students.map((student) => {
              const stageConfig = getStageConfig(student.current_stage);
              const stageIndex = getStageIndex(student.current_stage);
              
              return (
                <Card
                  key={student.id}
                  className="relative cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 border overflow-hidden group"
                  style={{ backgroundColor: COLORS.cardBg, borderColor: COLORS.border }}
                  onClick={() => openStudentProfile(student)}
                  data-testid={`admission-tile-${student.id}`}
                >
                  {/* Stage Progress Bar - Top */}
                  <div className="h-1 bg-gray-100 flex">
                    {STAGES.map((stage, idx) => (
                      <div
                        key={stage.key}
                        className={`flex-1 transition-all ${
                          idx <= stageIndex ? 'bg-gradient-to-r from-indigo-500 to-purple-500' : ''
                        }`}
                      />
                    ))}
                  </div>

                  <CardContent className="p-3">
                    {/* Row 1: Name + Student ID */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm truncate" style={{ color: COLORS.primary }}>
                          {student.full_name}
                        </h3>
                        <p className="text-[10px] font-mono" style={{ color: COLORS.accent }}>
                          {student.student_id}
                        </p>
                      </div>
                      <Badge 
                        className={`${stageConfig.color} text-[9px] px-1.5 py-0.5 border shrink-0`}
                        onClick={(e) => openStageDialog(e, student)}
                        data-testid={`stage-badge-${student.id}`}
                      >
                        {stageConfig.short}
                      </Badge>
                      {student.power_hours_bonus_eligible && (
                        <Badge
                          className="bg-amber-100 text-amber-800 border-amber-300 text-[9px] px-1.5 py-0.5 border shrink-0 ml-1"
                          title={`Power Hours conversion — +50% bonus eligible${student.power_hours_claimed_by_name ? ` (claimed by ${student.power_hours_claimed_by_name})` : ''}`}
                          data-testid={`ph-badge-${student.id}`}
                        >
                          ⚡ PH
                        </Badge>
                      )}
                      {student.date_of_birth && (() => {
                        const dob = new Date(student.date_of_birth);
                        const today = new Date();
                        let age = today.getFullYear() - dob.getFullYear();
                        const m = today.getMonth() - dob.getMonth();
                        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
                        if (age < 18) {
                          const turns18 = new Date(dob.getFullYear() + 18, dob.getMonth(), dob.getDate());
                          const daysUntil18 = Math.ceil((turns18 - today) / (1000 * 60 * 60 * 24));
                          return (
                            <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[9px] px-1.5 py-0.5 border shrink-0 ml-1" data-testid={`minor-badge-${student.id}`}>
                              MINOR • 18 in {daysUntil18}d
                            </Badge>
                          );
                        }
                        return null;
                      })()}
                    </div>

                    {/* Row 2: University + Country + Program */}
                    <div className="flex items-center gap-1.5 text-xs mb-1.5" style={{ color: COLORS.textSecondary }}>
                      <Building2 className="w-3 h-3 shrink-0" />
                      <span className="truncate">{student.university_name || 'University N/A'}</span>
                    </div>

                    {(student.destination_country || student.desired_country || student.country) && (
                      <div className="flex items-center gap-1.5 text-xs mb-1.5" style={{ color: COLORS.textSecondary }}>
                        <MapPin className="w-3 h-3 shrink-0" />
                        <span className="truncate">{student.destination_country || student.desired_country || student.country}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-1.5 text-xs mb-1.5" style={{ color: COLORS.textSecondary }}>
                      <GraduationCap className="w-3 h-3 shrink-0" />
                      <span className="truncate">{student.course || student.program || student.desired_course || 'Program N/A'}</span>
                    </div>

                    {/* Row 3: Intake + Counsellor */}
                    <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t" style={{ borderColor: COLORS.border }}>
                      <div className="flex items-center gap-1.5 text-[10px] min-w-0" style={{ color: COLORS.textSecondary }}>
                        <Calendar className="w-3 h-3 shrink-0" />
                        <span className="truncate">{student.intake || 'No Intake'}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-[10px] min-w-0" style={{ color: COLORS.textSecondary }}>
                        <User className="w-3 h-3 shrink-0" />
                        <span className="truncate">{student.assigned_counsellor_name || 'Unassigned'}</span>
                      </div>
                    </div>

                    {/* Row 4: Updated Date + Actions */}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1 text-[10px]" style={{ color: COLORS.textSecondary }}>
                        <Clock className="w-3 h-3" />
                        <span>{formatDate(student.updated_at)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {/* Reassign Button - Admin Only */}
                        {isAdmin && (
                          <button
                            onClick={(e) => openReassignDialog(e, student)}
                            className="p-2 sm:p-1.5 rounded hover:bg-orange-50 text-orange-500 hover:text-orange-600 transition-colors"
                            title="Reassign to another counselor"
                            data-testid={`reassign-btn-${student.id}`}
                          >
                            <i className="fas fa-user-edit text-xs"></i>
                          </button>
                        )}
                        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-indigo-500 transition-colors" />
                      </div>
                    </div>

                    {/* Quick Actions - Mobile Optimized (tap-friendly, mirrors Leads cards) */}
                    <div className="flex gap-1.5 mt-2 pt-1.5" style={{ borderTop: `1px solid ${COLORS.border}` }}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 h-9 sm:h-7 text-xs sm:text-[10px] px-2"
                        style={{ backgroundColor: '#EEF2FF', borderColor: '#C7D2FE', color: '#4F46E5' }}
                        onClick={(e) => openStageDialog(e, student)}
                        data-testid={`update-stage-btn-${student.id}`}
                      >
                        <GraduationCap className="w-3.5 h-3.5 sm:w-3 sm:h-3 mr-1" />
                        Update Stage
                      </Button>
                      {student.phone && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 h-9 sm:h-7 text-xs sm:text-[10px] px-2"
                          style={{ backgroundColor: '#ECFDF5', borderColor: '#A7F3D0', color: '#059669' }}
                          onClick={(e) => { e.stopPropagation(); window.open(`tel:${student.phone}`, '_self'); }}
                          data-testid={`call-btn-${student.id}`}
                        >
                          <Phone className="w-3.5 h-3.5 sm:w-3 sm:h-3 mr-1" />
                          Call
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              className="h-8"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Prev
            </Button>
            <span className="text-xs px-3 py-1.5 rounded-lg bg-gray-100" style={{ color: COLORS.textSecondary }}>
              {pagination.page} / {pagination.total_pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page >= pagination.total_pages}
              className="h-8"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>

      {/* Stage Update Dialog */}
      <Dialog open={stageDialogOpen} onOpenChange={setStageDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5" style={{ color: COLORS.accent }} />
              Update Stage
            </DialogTitle>
            <DialogDescription>
              {selectedStudent?.full_name} - {selectedStudent?.student_id}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-2 py-4">
            {STAGES.map((stage, idx) => {
              const currentIndex = selectedStudent ? getStageIndex(selectedStudent.current_stage) : -1;
              const isCompleted = idx < currentIndex;
              const isCurrent = idx === currentIndex;
              const isNext = idx === currentIndex + 1;
              
              return (
                <Button
                  key={stage.key}
                  variant={isCurrent ? "default" : "outline"}
                  className={`h-auto py-2 px-3 justify-start ${
                    isCurrent ? 'bg-indigo-600 text-white' :
                    isCompleted ? 'bg-green-50 border-green-200 text-green-700' :
                    isNext ? 'border-indigo-300 text-indigo-700' : ''
                  }`}
                  onClick={() => handleStageUpdate(stage.key)}
                  disabled={idx > currentIndex + 1}
                  data-testid={`stage-option-${stage.key}`}
                >
                  <span className="mr-2">{stage.icon}</span>
                  <span className="text-xs">{stage.label}</span>
                </Button>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setStageDialogOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Counselor Dialog */}
      <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <i className="fas fa-user-edit"></i>
              Reassign Lead
            </DialogTitle>
            <DialogDescription>
              Reassign {reassignStudent?.full_name} ({reassignStudent?.student_id}) to another counselor
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                Current Counselor
              </label>
              <div className="px-3 py-2 bg-gray-50 rounded-md text-sm text-gray-600">
                {reassignStudent?.assigned_counsellor_name || 'Unassigned'}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                New Counselor <span className="text-red-500">*</span>
              </label>
              <select
                value={newCounsellorId}
                onChange={(e) => setNewCounsellorId(e.target.value)}
                className="w-full h-10 text-sm bg-white border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                data-testid="reassign-counsellor-select"
              >
                <option value="">Select a counselor</option>
                {counsellors.filter(c => c.id !== reassignStudent?.assigned_counsellor_id).map(c => (
                  <option key={c.id} value={c.id}>{c.full_name}</option>
                ))}
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleReassign} 
              disabled={reassigning || !newCounsellorId}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {reassigning ? (
                <><i className="fas fa-spinner fa-spin mr-2"></i> Reassigning...</>
              ) : (
                <><i className="fas fa-user-edit mr-2"></i> Reassign</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Student Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Student to Admissions</DialogTitle>
            <DialogDescription>
              Add a student directly into the admission process. All fields marked * are required.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Full Name *</Label>
                <Input data-testid="add-full-name" value={newStudent.full_name} onChange={(e) => setNewStudent({...newStudent, full_name: e.target.value})} placeholder="Student full name" />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input data-testid="add-phone" value={newStudent.phone} onChange={(e) => setNewStudent({...newStudent, phone: e.target.value})} placeholder="Phone number" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email *</Label>
                <Input data-testid="add-email" type="email" value={newStudent.email} onChange={(e) => setNewStudent({...newStudent, email: e.target.value})} placeholder="Email address" />
              </div>
              <div>
                <Label>Date of Birth *</Label>
                <Input data-testid="add-dob" type="date" value={newStudent.date_of_birth} onChange={(e) => setNewStudent({...newStudent, date_of_birth: e.target.value})} max={new Date().toLocaleDateString('en-CA')} />
                {newStudent.date_of_birth && (() => {
                  const dob = new Date(newStudent.date_of_birth);
                  const today = new Date();
                  let age = today.getFullYear() - dob.getFullYear();
                  const m = today.getMonth() - dob.getMonth();
                  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
                  if (age < 18) {
                    const turns18 = new Date(dob.getFullYear() + 18, dob.getMonth(), dob.getDate());
                    const daysUntil18 = Math.ceil((turns18 - today) / (1000 * 60 * 60 * 24));
                    return <p className="mt-1 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">Minor ({age} yrs) — Turns 18 in {daysUntil18} days • Additional docs required</p>;
                  }
                  return <p className="mt-1 text-xs text-gray-400">Age: {age} years</p>;
                })()}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Program / Course *</Label>
                <CatalogSelect
                  type="program"
                  value={newStudent.desired_course}
                  onChange={(v) => setNewStudent({ ...newStudent, desired_course: v })}
                  user={user}
                  testId="add-course"
                />
              </div>
              <div>
                <Label>Country *</Label>
                <CatalogSelect
                  type="country"
                  value={newStudent.desired_country}
                  onChange={(v) => setNewStudent({ ...newStudent, desired_country: v })}
                  user={user}
                  testId="add-country"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>University *</Label>
                <CatalogSelect
                  type="university"
                  value={newStudent.university_selected}
                  onChange={(v) => setNewStudent({ ...newStudent, university_selected: v })}
                  user={user}
                  testId="add-university"
                />
              </div>
              <div>
                <Label>Intake *</Label>
                <CatalogSelect
                  type="intake"
                  value={newStudent.intake}
                  onChange={(v) => setNewStudent({ ...newStudent, intake: v })}
                  user={user}
                  testId="add-intake"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Source *</Label>
                <Select value={newStudent.source} onValueChange={(v) => setNewStudent({...newStudent, source: v})}>
                  <SelectTrigger data-testid="add-source">
                    <SelectValue placeholder="Where is this lead from?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direct">Direct Enquiry</SelectItem>
                    <SelectItem value="referral">Referral / Reference</SelectItem>
                    <SelectItem value="walk_in">Walk-in</SelectItem>
                    <SelectItem value="agent">Agent / Sub-agent</SelectItem>
                    <SelectItem value="website">Website</SelectItem>
                    <SelectItem value="social_media">Social Media</SelectItem>
                    <SelectItem value="event">Event / Seminar</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Assign Counselor</Label>
                <Select value={newStudent.assigned_counselor_id || 'unassigned'} onValueChange={(v) => setNewStudent({...newStudent, assigned_counselor_id: v === 'unassigned' ? '' : v})}>
                  <SelectTrigger data-testid="add-counselor">
                    <SelectValue placeholder="Select counselor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {[...counsellors].sort((a, b) => (b.is_available === true ? 1 : 0) - (a.is_available === true ? 1 : 0)).map(c => (
                      <SelectItem key={c.id} value={c.id} disabled={c.is_available === false}>
                        {c.full_name} {c.is_available === false ? '(Unavailable)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notes for Documentation Team</Label>
              <Textarea data-testid="add-notes" value={newStudent.notes} onChange={(e) => setNewStudent({...newStudent, notes: e.target.value})} placeholder="Any additional notes..." rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-add-student" onClick={handleAddStudent} disabled={addingStudent} className="bg-teal-600 hover:bg-teal-700">
              {addingStudent ? 'Adding...' : 'Add to Admissions'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
